import React, { useEffect, useMemo, useState } from 'react';
import PageContainer from '../../layouts/PageContainer';
import PageHeader from '../../components/Nav/PageHeader';
import { useNetwork } from '../../DataStore/NetworkContext';
import ajaxNetwork from '../../util/remote/ajaxNetwork';
import SearchDeviceModal from '../../components/Devices/SearchDeviceModal';
import { ConnectionsListSkeleton, LeftCardSkeleton, VouchersTableSkeleton } from '../../components/Devices/ContentLoaders';

/* =============== helpers =============== */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtShortDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : '—';
const fmtBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};
const fmtUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
const secsToHms = (s) => {
  const sec = Math.max(0, s | 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const r = sec % 60;
  return [h, m, r].map(v => String(v).padStart(2, '0')).join(':');
};
const badge = (status) => {
  const tone = status === 'Connected' || status === 'connected' || status === 'used' ? 'success'
    : status === 'Expired' || status === 'Revoked' || status === 'revoked' ? 'danger'
    : status === 'Pending' ? 'warning'
    : 'secondary';
  return <span className={`badge badge-${tone}`} style={{textTransform: 'capitalize'}}>{String(status || '—')}</span>;
};

/* normalize to AA:BB:CC:DD:EE:FF */
const normalizeMac = (val) => {
  if (!val) return '';
  const hex = val.replace(/[^A-Fa-f0-9]/g, '').toUpperCase().slice(0, 12);
  const parts = hex.match(/.{1,2}/g) || [];
  return parts.join(':');
};
const isValidMac = (mac) => /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(mac);

/* map context deviceInfo -> view model the page expects */
const toViewModel = (info) => {
  if (!info) return null;
  const voucherHistory = Array.isArray(info.voucher_history) ? info.voucher_history : [];
  const connections = Array.isArray(info.connection_history) ? info.connection_history : [];
  return {
    mac: info.mac_address || '—',
    status: info.router_status_message || 'Disconnected',
    vendor: info.device_info?.manufacturer || '—',
    osHint: info.device_info?.os || '—',
    nickname: info.device_info?.nickname || '',
    deviceName: info.device_name || '—',
    deviceToken: info.device_token || null,
    totalSessions: info.session_count ?? 0,
    totalDownload: 0,
    totalUpload: 0,
    lastSeen: info.heart_beat,
    firstSeen: info.created_at,
    lastIp: info.last_ip,
    formattedDownload: info.formatted_download || null,
    formattedUpload: info.formatted_upload || null,
    totalConnections: info.total_connections ?? 0,
    activeVoucher: info.active_voucher || null,
    vouchers: [

      ...voucherHistory.map(v => ({
        code: v.code,
        plan: v.plan_name,
        amount: v.amount,
        purchasedAt: v.first_used_at,
        expiresAt: v.expires_at,
        usedDevices: v.usage_count ?? 1,
        status: v.status
      }))
    ],
    connections: connections.map(c => ({
      start: c.used_at,
      stop: c.used_at,
      location: c.router.name + '—' + (c.router.site || 'Unknown Location'),
      nas: c.session_id ? `Session ${c.session_id}` : (c.status || '—'),
      ip: c.ip_address || '—',
      voucher: c.voucher_code || '—',
      bytesDown: c.bytes_down ?? 0,
      bytesUp: c.bytes_up ?? 0,
      duration: c.duration_seconds ?? 0,
      rejectReason: c.reject_reason || '—'
    }))
  };
};



/* =============== page =============== */
const DeviceHistoryPage = () => {
  const { selectedDevice, setSelectedDevice } = useNetwork();

  const [activeTab, setActiveTab] = useState('vouchers');
  const [searchMac, setSearchMac] = useState(selectedDevice || '');
  const [searchError, setSearchError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [device, setDevice] = useState(null);
  const [nickname, setNickname] = useState('');
  const [savedNick, setSavedNick] = useState('');
  const [isEditingNick, setIsEditingNick] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [savedDeviceName, setSavedDeviceName] = useState('');

  const [showResults, setShowResults] = useState(false);
  const [macResults, setMacResults] = useState(null);


  const [deviceInfo, setDeviceInfo] = useState(null);
  const [fetchingDevice, setFetchingDevice] = useState(false);

  // refresh when context changes
  useEffect(() => {
    const vm = toViewModel(deviceInfo);
    setDevice(vm);
    setNickname(vm?.nickname || '');
    setSavedNick(vm?.nickname || '');
    setDeviceName(vm?.deviceName || '');
    setSavedDeviceName(vm?.deviceName || '');
    if (vm) setActiveTab('vouchers');
    setIsFetching(false); // clear skeleton when new data lands
  }, [deviceInfo]);

  useEffect(() => {
      const macFromUrl = new URLSearchParams(window.location.search).get('m') || null;
      if(macFromUrl) {
        getDeviceProfile(macFromUrl);
      }

  }, []);

  const recentVoucher = useMemo(() => (device?.activeVoucher ? device.activeVoucher : null), [device]);

  const onSaveNickname = async(e) => {
    e?.preventDefault?.();
    if(deviceName.length === 0 || deviceName.trim() === savedDeviceName) {
      setIsEditingNick(false);
      return;
    }
    setSaving(true);
    const server_response = await ajaxNetwork.saveDeviceName(device.deviceToken, deviceName.trim());
    if(server_response.status === "OK"){
      // refresh device info
      await getDeviceProfile(device.deviceToken);
    }

    setTimeout(() => {
      setSavedDeviceName((deviceName || '').trim());
      setIsEditingNick(false);
      setSaving(false);
    }, 400);
  };

  const handleMacInput = (e) => {
    const next = normalizeMac(e.target.value);
    setSearchMac(next);
    if (searchError) setSearchError('');
  };

  const doSearch = async(e) => {
    e?.preventDefault?.();
    const mac = normalizeMac(searchMac);
    // if (!isValidMac(mac)) {
    //   setSearchError('Enter a valid MAC (e.g., AA:BB:CC:DD:EE:FF)');
    //   return;
    // }
    setSearchError('');
    setIsFetching(true);
    setShowResults(true);
    await getMacResults(mac);
    setIsFetching(false);
  };

  const startEditNick = () => { setNickname((savedNick || '').trim()); setIsEditingNick(true); };
  const cancelEditNick = () => { setNickname(savedNick || ''); setIsEditingNick(false); };
  const onNickKeyDown = (e) => { if (e.key === 'Enter') onSaveNickname(e); if (e.key === 'Escape') cancelEditNick(); };

  const showSkeleton = fetchingDevice;

  const getMacResults = async (mac) => {
    const response =  await ajaxNetwork.searchMac(mac);
    if(response.status === "OK"){
      setMacResults(response.details);
    } else {
      setMacResults('404');
    }
  }

  const getDeviceProfile = async (device_token) => {
    setShowResults(false);
    setDeviceInfo(null);
    setFetchingDevice(true);
    const server_response = await ajaxNetwork.fetchDeviceInfo(device_token);
    setFetchingDevice(false);
    if(server_response.status === "OK"){
      setDeviceInfo(server_response.details)
    } else {
      setDeviceInfo("404");
    }
    
  };


  return (<>
    <PageContainer>
      <PageHeader
        title="Device Profile"
        breadcrumb={
          <>
            <li className="breadcrumb-item">Devices</li>
            <li className="breadcrumb-item active" aria-current="page">Device Profile</li>
          </>
        }
      />

      {/* Search */}
      <div className="card mb-3 shadow-sm border-0">
        <div className="card-body py-3">
          <form onSubmit={doSearch} className="d-flex align-items-center">
            <div className="mr-3 text-muted small">Search MAC</div>
            <div className="input-group" style={{ maxWidth: 420 }}>
              <div className="input-group-prepend">
                <span className="input-group-text bg-dar border-0 text-muted"><i className="fe fe-search" /></span>
              </div>
              <input
                className="form-control bg-dark border-0 text-light"
                value={searchMac}
                onChange={handleMacInput}
                placeholder="Enter MAC address (e.g., AA:BB:CC:DD:EE:FF)"
                spellCheck={false}
                disabled={isFetching}
              />
              <div className="input-group-append">
                <button className="btn btn-primary" type="submit" disabled={isFetching}>
                  {isFetching ? 'Searching…' : 'Find'}
                </button>
              </div>
            </div>
            {searchError ? <div className="text-danger small ml-3">{searchError}</div> : null}
          </form>
        </div>
      </div>

      <div className="row row-sm">
        {/* Left column */}
        <div className="col-lg-4">
          {showSkeleton ? (
            <LeftCardSkeleton />
          ) : (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <div className="text-uppercase text-muted small">MAC ADDRESS</div>
                    <h5 className="mb-0 font-weight-bold">{device?.mac || '—'}</h5>
                  </div>
                  <div>{device ? badge(device.status) : null}</div>
                </div>

                <div className="d-flex align-items-center mt-3">
                  <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                    <i className="fe fe-smartphone" />
                  </div>
                  <div className="ml-3">
                    <div className="mb-0 font-weight-600">{device?.vendor || '—'}</div>
                    <div className="text-muted small">{device?.osHint || '—'}</div>
                  </div>
                </div>

                <hr />
                <div className="mb-2">
                  <label className="small text-muted mb-1 d-flex align-items-center justify-content-between">
                    <span>Nickname</span>
                    {!isEditingNick && (
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={startEditNick} disabled={!device}>
                        Edit
                      </button>
                    )}
                  </label>

                  {!isEditingNick && (
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="text-truncate pr-2">
                        {savedNick ? <strong>{savedNick}</strong> : <span className="text-muted">No nickname</span>}
                      </div>
                    </div>
                  )}

                  {isEditingNick && (
                    <form className="mt-1" onSubmit={onSaveNickname}>
                      <div className="input-group">
                        <input
                          className="form-control"
                          value={deviceName}
                          onChange={(e) => setDeviceName(e.target.value)}
                          onKeyDown={onNickKeyDown}
                          placeholder="Manufacturer;OS;Device name"
                          autoFocus
                          disabled={!device || saving}
                        />
                        <div className="input-group-append">
                          <button className="btn btn-primary" type="submit" disabled={!device || saving || deviceName.trim() === savedDeviceName}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={cancelEditNick} disabled={saving}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {savedNick && !isEditingNick && (
                    <div className="small text-success mt-1">
                      Saved as <strong>{savedNick}</strong>
                    </div>
                  )}
                </div>

                <div className="row text-center mt-3">
                  <div className="col-4">
                    <div className="text-muted small">Sessions</div>
                    <div className="h5 mb-0">{device?.totalSessions ?? '—'}</div>
                  </div>
                  <div className="col-4">
                    <div className="text-muted small">Download</div>
                    <div className="h6 mb-0">{device ? (device.formattedDownload || '—') : '—'}</div>
                  </div>
                  <div className="col-4">
                    <div className="text-muted small">Upload</div>
                    <div className="h6 mb-0">{device ? (device.formattedUpload || '—') : '—'}</div>
                  </div>
                </div>

                <div className="mt-3 small">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Last seen</span>
                    <span>{fmtDate(device?.lastSeen)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">First seen</span>
                    <span>{fmtShortDate(device?.firstSeen)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Last IP</span>
                    <span>{device?.lastIp || '—'}</span>
                  </div>
                </div>

                <div className="mt-4 p-3 rounded" style={{ background: 'linear-gradient(135deg, #1f2430, #1a1f2b)' }}>
                  <div className="d-
                  flex justify-content-between align-items-center">
                    <div>
                      <div className="small text-muted">Current Voucher</div>
                      <div className="font-weight-600">{recentVoucher?.voucher_code || '—'}</div>
                      <div className="small text-muted">{recentVoucher?.plan_name || '—'}</div>
                    </div>
                    <div className="text-right">
                      <div>{recentVoucher ? badge(recentVoucher.status) : null}</div>
                      {recentVoucher?.expiresAt && (
                        <div className="small text-muted mt-1">
                          Expires {fmtShortDate(recentVoucher.expires_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="col-lg-8">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-transparent border-0 pb-0">
              <ul className={`nav nav-pills pill-seg ${activeTab === 'connections' ? 'seg-right' : ''}`}>
                <span className="pill-glider" aria-hidden="true" />
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link btn-sm ${activeTab === 'vouchers' ? 'active' : 'btn-light'}`}
                    onClick={() => setActiveTab('vouchers')}
                    disabled={isFetching}
                  >
                    <i className="fe fe-tag mr-2" />
                    Vouchers
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link btn-sm ${activeTab === 'connections' ? 'active' : 'btn-light'}`}
                    onClick={() => setActiveTab('connections')}
                    disabled={isFetching}
                  >
                    <i className="fe fe-activity mr-2" />
                    Connections
                  </button>
                </li>
              </ul>
            </div>

            <div className="card-body pt-2">
              {showSkeleton ? (
                activeTab === 'vouchers' ? <VouchersTableSkeleton /> : <ConnectionsListSkeleton />
              ) : (
                <>
                  {activeTab === 'vouchers' && (
                    <div className="table-responsive overflow-auto" style={{ maxHeight: '400px' }}>
                      <table className="table table-hover table-sm align-middle mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{minWidth: 120}}>Voucher</th>
                            <th>Plan</th>
                            <th className="text-right">Amount</th>
                            <th style={{minWidth: 150}}>First Used</th>
                            <th style={{minWidth: 150}}>Expires</th>
                            <th className="text-center">Usages</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {device?.vouchers?.map(v => (
                            <tr key={`${v.code}-${v.purchasedAt}`}>
                              <td className="font-weight-600">{v.code}</td>
                              <td>{v.plan}</td>
                              <td className="text-right">{fmtUGX(v.amount)}</td>
                              <td>{fmtDate(v.purchasedAt)}</td>
                              <td>{v.expiresAt ? fmtDate(v.expiresAt) : '—'}</td>
                              <td className="text-center">{v.usedDevices ?? '—'}</td>
                              <td>{badge(v.status)}</td>
                            </tr>
                          ))}
                          {!device?.vouchers?.length && (
                            <tr><td colSpan={7} className="text-center text-muted py-4">No vouchers for this device.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {activeTab === 'connections' && (
                    <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '400px' }}>
                      {device?.connections?.map((c, idx) => {
                        const dur = (new Date(c.stop) - new Date(c.start)) / 1000;
                        return (
                          <div key={idx} className="list-group-item px-0 py-3">
                            <div className="d-flex justify-content-between">
                              <div>
                                <div className="d-flex align-items-center">
                                  <span className="badge badge-light mr-2">{secsToHms(c.duration)}</span>
                                  <strong className="mr-2">{c.location}</strong>
                                  <span className="text-muted small">({c.nas})</span>
                                </div>
                                <div className="text-muted small">
                                  {fmtDate(c.start)} • IP {c.ip} • Voucher {c.voucher}
                                  <br/>{c.rejectReason !== '—' ? `Rejected: ${c.rejectReason}` : ''}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="small">↓ {fmtBytes(c.bytesDown)}</div>
                                <div className="small">↑ {fmtBytes(c.bytesUp)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!device?.connections?.length && (
                        <div className="text-muted small py-4">No connection history for this device.</div>
                      )}
                      {device?.connections?.length ? (
                        <div className="text-muted small mt-3">
                          Showing {device.connections.length} of {device.totalConnections ?? device.connections.length} sessions.
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="text-muted small mt-3">
            Tip: Give this device a nickname to recognize it easily across vouchers and sessions.
            <br />Device FingerPrint: {device?.deviceToken || '—'}
          </div>
        </div>
      </div>
    </PageContainer>
    <SearchDeviceModal
      show={showResults}
      onClose={() => setShowResults(false)}
      macAddress={searchMac}
      results={macResults}
      loading={isFetching}
      onSelectDevice={getDeviceProfile}
    />
    </>
  );
};

export default DeviceHistoryPage;
